// @ts-nocheck
import React from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalNavigation from '../components/common/GlobalNavigation';
import { KeyboardShortcutsModal } from '../components/common/KeyboardShortcutsModal';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useSwitchUser } from '../contexts/SwitchUserContext';
import CustomerServiceChatbot from '../components/common/CustomerServiceChatbot';
import { persistedClientRecords } from '../services/mockData/clients';
import { persistedTaskRecords } from '../services/mockData/tasks';
import { useGobodoRecords } from '../services/queries/gobodoRecordQueries';
import { shouldUseGobodo } from '../utils/gobodoUtils';
import { AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMVPDefaultRole } from '../config/mvpConfig';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const { isSwitched } = useSwitchUser();
  const { isShortcutsModalOpen, setIsShortcutsModalOpen } = useKeyboardShortcuts();
  const contentPadding24 = isSwitched ? 'pt-[140px]' : 'pt-24';

  const getUserRole = (): string => {
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };

  const userRole = getUserRole();

  const handleAppSelect = (app: any) => {
    if (app.name === 'home') {
      navigate('/');
    } else {
      navigate(`/${app.name.toLowerCase().replace(' ', '-')}`);
    }
  };

  const handleNavigateToTask = (taskId: string) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleNavigateToCase = (caseId: string) => {
    navigate(`/cases/${caseId}`);
  };

  const shouldGobodo = shouldUseGobodo();
  const { data: gobodoTasks } = useGobodoRecords('Task__c');
  const { data: gobodoClients } = useGobodoRecords('Client__c');

  const getCurrentUserTasks = () => {
    const allTasks = (shouldGobodo && gobodoTasks) ? gobodoTasks : persistedTaskRecords.get();
    return allTasks.filter((task: any) => {
      const title = task.title ?? task.Subject__c ?? task.subject;
      const status = task.status ?? task.Status__c;
      if (!task || !task.id || !title || !status) return false;
      const assignee = task.assignee ?? task.Assignee__c;
      return assignee === 'John Smith';
    });
  };

  const taskStatus = (t: any) => t?.status ?? t?.Status__c;
  const taskDueDate = (t: any) => t?.dueDate ?? t?.DueDate__c;
  const getOpenTasks = () => getCurrentUserTasks().filter(task => taskStatus(task) !== 'Completed');
  const getDueSoonTasks = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const threeDaysFromNow = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);
    return getCurrentUserTasks().filter((task: any) => {
      if (taskStatus(task) === 'Completed') return false;
      const due = taskDueDate(task);
      if (!due) return false;
      const dueDate = new Date(due);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate >= today && dueDate <= threeDaysFromNow;
    });
  };
  const getCompletedTasks = () => getCurrentUserTasks().filter(task => taskStatus(task) === 'Completed');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <GlobalNavigation
        currentView="home"
        onNavigate={(view) => {
          if (view === 'home') {
            navigate('/');
          } else {
            navigate(`/${view}`);
          }
        }}
        userRole={userRole}
        onRoleChange={() => {}}
        showMainNavigation={true}
        onAppSelect={handleAppSelect}
        onNavigateToTask={handleNavigateToTask}
        onNavigateToCase={handleNavigateToCase}
      />

      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${contentPadding24}`}>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Work</h1>
          <p className="text-gray-600 dark:text-gray-400">Your personal task overview and assignments</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open Tasks</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getOpenTasks().length}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Due Soon</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getDueSoonTasks().length}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getCompletedTasks().length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Priority</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getCurrentUserTasks().filter(t => t.priority === 'High' && t.status !== 'Completed').length}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Open Tasks</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {getOpenTasks().slice(0, 5).map((task) => (
                  <button
                    key={task.id}
                    onClick={() => handleNavigateToTask(task.id)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer text-left"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{task.type} • Due: {task.dueDate}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      task.priority === 'High' ? 'bg-red-100 text-red-800' :
                      task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority}
                    </span>
                  </button>
                ))}
                {getOpenTasks().length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No open tasks</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Due Soon</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {getDueSoonTasks().slice(0, 5).map((task) => (
                  <button
                    key={task.id}
                    onClick={() => handleNavigateToTask(task.id)}
                    className="w-full flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors cursor-pointer text-left"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{task.type} • Due: {task.dueDate}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      task.priority === 'High' ? 'bg-red-100 text-red-800' :
                      task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority}
                    </span>
                  </button>
                ))}
                {getDueSoonTasks().length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No tasks due soon</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Activity</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {getCurrentUserTasks().slice(0, 8).map((task) => (
                <button
                  key={task.id}
                  onClick={() => handleNavigateToTask(task.id)}
                  className="w-full flex items-start space-x-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer text-left"
                >
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    task.status === 'Completed' ? 'bg-green-500' :
                    task.status === 'In Progress' ? 'bg-blue-500' :
                    'bg-yellow-500'
                  }`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {task.type} •
                      <span className={`${
                        task.status === 'Completed' ? 'text-green-600 dark:text-green-400' :
                        task.status === 'In Progress' ? 'text-blue-600 dark:text-blue-400' :
                        'text-yellow-600 dark:text-yellow-400'
                      }`}>
                        {' '}{task.status}
                      </span>
                      • Due: {task.dueDate}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    task.priority === 'High' ? 'bg-red-100 text-red-800' :
                    task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {task.priority}
                  </span>
                </button>
              ))}
              {getCurrentUserTasks().length === 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">No recent activity</p>
              )}
            </div>
          </div>
        </div>
      </main>

      {(userRole === 'customer_service' || userRole === 'supervisor' || userRole === 'manager' || userRole === 'cb_supervisor' || userRole === 'cm_supervisor' || userRole === 'system_admin') && (
        <CustomerServiceChatbot
          userRole={userRole}
          userName="CS Agent"
          userDepartment="Customer Service"
          onClientLookup={(clientId) => {
            const allClients = (shouldGobodo && gobodoClients) ? gobodoClients : persistedClientRecords.get();
            const client = allClients.find((c: any) => c.id === clientId);
            if (client) {
              navigate(`/clients/${clientId}`);
            }
          }}
          onEscalation={(issue) => {
            console.log('Escalation requested:', issue);
          }}
          onTransfer={(department) => {
            console.log('Transfer to department:', department);
          }}
          position="bottom-right"
        />
      )}

      <KeyboardShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={() => setIsShortcutsModalOpen(false)}
      />
    </div>
  );
};

export default HomePage;
